import React, { useEffect, useMemo, useState } from "react";
import { api, API } from "../lib/api";
import { useAccounts } from "../lib/account";
import { moneyFor } from "../lib/currency";
import useReveal from "../lib/useReveal";
import {
  AreaChart, Area, BarChart, Bar, LineChart, Line,
  XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from "recharts";
import {
  TrendUp, TrendDown, ChartLine, Target, Trophy, DownloadSimple,
  CaretLeft, CaretRight, Funnel, Warning,
} from "@phosphor-icons/react";
import { QRCodeCanvas } from "qrcode.react";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import AddTradeDialog from "../components/AddTradeDialog";
import CSVImportDialog from "../components/CSVImportDialog";
import DisciplineCard from "../components/DisciplineCard";
import ReportsCard from "../components/ReportsCard";
import ForexNews from "../components/ForexNews";
import CryptoHeatmap from "../components/CryptoHeatmap";
import TradesRangeDialog from "../components/TradesRangeDialog";
import MarketHours from "../components/MarketHours";
import TimingChart from "../components/TimingChart";
import OnboardingTour, { shouldShowOnboarding } from "../components/OnboardingTour";
import { useAuth } from "../lib/auth";
import { useNavigate } from "react-router-dom";

const OVERTRADE_THRESHOLD = 7;

function fmtISO(d) {
  // toISOString() shifts back a day for positive UTC offsets (e.g. IST)
  // because it converts LOCAL midnight to UTC. Build the string from local
  // getters so the calendar cell keyed as "Sep 2" stays "2026-09-02" and
  // matches the backend's `_session_date` which slices UTC ISO strings.
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}
function startOfWeek(d) { const x = new Date(d); const dow = (x.getDay() + 6) % 7; x.setDate(x.getDate() - dow); return x; }
function endOfWeek(d) { const s = startOfWeek(d); const e = new Date(s); e.setDate(s.getDate() + 6); return e; }

function computeRange(preset, custom) {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  switch (preset) {
    case "today": return { from: fmtISO(today), to: fmtISO(today) };
    case "week":  return { from: fmtISO(startOfWeek(today)), to: fmtISO(endOfWeek(today)) };
    case "month": {
      const s = new Date(today.getFullYear(), today.getMonth(), 1);
      const e = new Date(today.getFullYear(), today.getMonth() + 1, 0);
      return { from: fmtISO(s), to: fmtISO(e) };
    }
    case "last_month": {
      const s = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      const e = new Date(today.getFullYear(), today.getMonth(), 0);
      return { from: fmtISO(s), to: fmtISO(e) };
    }
    case "ytd":    return { from: `${today.getFullYear()}-01-01`, to: fmtISO(today) };
    case "custom": return { from: custom.from || "", to: custom.to || "" };
    default:       return { from: "", to: "" };
  }
}

function Kpi({ label, value, sub, tone = "default", testid, icon: Icon }) {
  const tones = { up: "text-emerald-400", down: "text-red-500", default: "text-foreground" };
  return (
    <div className="rounded-2xl border border-border bg-card p-4 sm:p-6 lg:p-7 transition-transform duration-200 hover:-translate-y-0.5">
      <div className="flex items-center justify-between">
        <p className="text-[10px] sm:text-[11px] tracking-[0.2em] sm:tracking-[0.25em] uppercase text-muted-foreground">{label}</p>
        {Icon && <Icon size={18} className="text-muted-foreground shrink-0" />}
      </div>
      <p data-testid={testid} className={`font-mono-num text-2xl sm:text-3xl lg:text-5xl font-bold mt-2 sm:mt-3 tracking-tight ${tones[tone]} truncate`}>{value}</p>
      {sub && <p className="text-[11px] sm:text-sm text-muted-foreground mt-1 sm:mt-2 truncate">{sub}</p>}
    </div>
  );
}

function CalendarView({ month, setMonth, cells, onOpenDay, account, range }) {
  const first = new Date(month.getFullYear(), month.getMonth(), 1);
  const last = new Date(month.getFullYear(), month.getMonth() + 1, 0);
  const startDow = (first.getDay() + 6) % 7;
  const days = [];
  for (let i = 0; i < startDow; i++) days.push(null);
  for (let d = 1; d <= last.getDate(); d++) days.push(new Date(month.getFullYear(), month.getMonth(), d));
  while (days.length < 42) days.push(null);
  const byDate = Object.fromEntries((cells || []).map(c => [c.date, c]));
  const monthPnl = (cells || []).reduce((s, c) => s + (c.pnl || 0), 0);
  const overtradeDays = (cells || []).filter(c => c.trades >= OVERTRADE_THRESHOLD).length;

  return (
    <div className="rounded-2xl border border-border bg-card p-4 sm:p-6" data-testid="calendar-view">
      <div className="flex items-center justify-between mb-4 gap-2">
        <div className="min-w-0">
          <p className="text-[10px] sm:text-[11px] tracking-[0.2em] sm:tracking-[0.25em] uppercase text-muted-foreground">Calendar</p>
          <h3 className="font-display text-lg sm:text-2xl font-semibold mt-1 truncate">
            {month.toLocaleString(undefined, { month: "long", year: "numeric" })}
          </h3>
          <div className="flex items-center flex-wrap gap-2 sm:gap-3 mt-2">
            <p className={`font-mono-num text-xl sm:text-2xl font-black tracking-tight ${monthPnl >= 0 ? "text-emerald-400" : "text-red-500"}`}
              data-testid="calendar-month-pnl">
              {moneyFor(account, monthPnl, { sign: true })}
            </p>
            {overtradeDays > 0 && (
              <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full bg-amber-500/15 text-amber-300"
                data-testid="overtrade-badge">
                <Warning size={10} /> {overtradeDays} over-trading day{overtradeDays > 1 ? "s" : ""}
              </span>
            )}
          </div>
        </div>
        <div className="flex gap-1">
          <Button variant="outline" size="icon" onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() - 1, 1))}
            data-testid="cal-prev"><CaretLeft size={16} /></Button>
          <Button variant="outline" size="sm" onClick={() => setMonth(new Date())} data-testid="cal-today">Today</Button>
          <Button variant="outline" size="icon" onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() + 1, 1))}
            data-testid="cal-next"><CaretRight size={16} /></Button>
        </div>
      </div>
      <div className="grid grid-cols-7 gap-1.5 text-[10px] text-muted-foreground mb-1">
        {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map(d => <div key={d} className="text-center">{d}</div>)}
      </div>
      <div className="grid grid-cols-7 gap-1.5">
        {days.map((d, i) => {
          if (!d) return <div key={i} className="aspect-square" />;
          const iso = fmtISO(d);
          const cell = byDate[iso];
          const pnl = cell?.pnl || 0;
          const trades = cell?.trades || 0;
          const hasTrades = trades > 0;
          const overtrade = trades >= OVERTRADE_THRESHOLD;
          const outOfRange = (range?.from && iso < range.from) || (range?.to && iso > range.to);
          const intensity = Math.min(1, Math.abs(pnl) / 5000);
          const bg = outOfRange ? "hsl(var(--secondary)/0.35)" :
                     pnl > 0 ? `rgba(16,185,129,${0.2 + intensity * 0.6})` :
                     pnl < 0 ? `rgba(239,68,68,${0.2 + intensity * 0.6})` : "hsl(var(--secondary))";
          return (
            <button key={iso} data-testid={`cal-${iso}${overtrade ? "-overtrade" : ""}`}
              disabled={!hasTrades || outOfRange}
              onClick={() => onOpenDay(iso, cell)}
              title={outOfRange ? `${iso} · outside filter range` : (hasTrades ? `${iso} · ${trades} trade${trades > 1 ? "s" : ""} · ${moneyFor(account, pnl)}${overtrade ? " · OVER-TRADING" : ""}` : iso)}
              className={`aspect-square rounded-md p-1.5 text-left flex flex-col justify-between transition-transform border overflow-hidden ${
                overtrade && !outOfRange ? "border-amber-400 ring-2 ring-amber-500/50" : "border-border/50"
              } ${outOfRange ? "opacity-25 cursor-not-allowed grayscale" : hasTrades ? "cursor-pointer hover:scale-105 hover:brightness-110" : "cursor-default opacity-70"}`}
              style={{ background: bg }}>
              <div className="flex items-center justify-between">
                <span className="text-[11px] font-semibold leading-none">{d.getDate()}</span>
                {overtrade && <Warning size={10} className="text-amber-300" />}
              </div>
              {hasTrades && (
                <div className="text-right leading-tight w-full min-w-0">
                  <p className={`text-[11px] sm:text-[12px] font-mono-num font-bold tabular-nums truncate ${pnl >= 0 ? "text-emerald-100" : "text-red-100"}`}
                    title={moneyFor(account, pnl, { sign: true })}>
                    {moneyFor(account, pnl, { sign: true, compact: true })}
                  </p>
                  <p className="text-[9px] text-white/70 truncate">{trades}×</p>
                </div>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function EquityChart({ data, chartType, dataKey = "equity" }) {
  const last = data.length ? data[data.length - 1][dataKey] : 0;
  const color = last >= 0 ? "#10b981" : "#ef4444";
  return (
    <div className="h-72" data-testid={`equity-chart-${dataKey}`}>
      <ResponsiveContainer width="100%" height="100%">
        {chartType === "bars" ? (
          <BarChart data={data}>
            <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="3 3" />
            <XAxis dataKey="date" stroke="hsl(var(--muted-foreground))" fontSize={11} />
            <YAxis stroke="hsl(var(--muted-foreground))" fontSize={11} />
            <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8 }} />
            <Bar dataKey={dataKey} fill={color} radius={[4, 4, 0, 0]} />
          </BarChart>
        ) : chartType === "line" ? (
          <LineChart data={data}>
            <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="3 3" />
            <XAxis dataKey="date" stroke="hsl(var(--muted-foreground))" fontSize={11} />
            <YAxis stroke="hsl(var(--muted-foreground))" fontSize={11} />
            <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8 }} />
            <Line type="monotone" dataKey={dataKey} stroke={color} strokeWidth={2} dot={false} />
          </LineChart>
        ) : (
          <AreaChart data={data}>
            <defs>
              <linearGradient id={`eqfill-${dataKey}`} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={color} stopOpacity={0.5} />
                <stop offset="100%" stopColor={color} stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="3 3" />
            <XAxis dataKey="date" stroke="hsl(var(--muted-foreground))" fontSize={11} />
            <YAxis stroke="hsl(var(--muted-foreground))" fontSize={11} />
            <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8 }} />
            <Area type="monotone" dataKey={dataKey} stroke={color} fill={`url(#eqfill-${dataKey})`} strokeWidth={2} />
          </AreaChart>
        )}
      </ResponsiveContainer>
    </div>
  );
}

export default function Dashboard() {
  useReveal();
  const { activeId, accounts } = useAccounts();
  const [summary, setSummary] = useState(null);
  const [calendar, setCalendar] = useState([]);
  const [addOpen, setAddOpen] = useState(false);
  const [csvOpen, setCsvOpen] = useState(false);
  const [shareUrl, setShareUrl] = useState("");
  const [shareOpen, setShareOpen] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [rangeOpen, setRangeOpen] = useState(false);
  const [range, setRange] = useState({ from: "", to: "", title: "", subtitle: "" });

  const [datePreset, setDatePreset] = useState("all");
  const [custom, setCustom] = useState({ from: "", to: "" });
  const [pnlType, setPnlType] = useState("net");
  const [chartType, setChartType] = useState("area");
  const [position, setPosition] = useState("all");
  const [layout, setLayout] = useState("classic");
  const [tourOpen, setTourOpen] = useState(false);
  const { user } = useAuth();
  const nav = useNavigate();

  // Show onboarding once per user (based on localStorage flag). Fires after
  // the auth user is loaded so we can greet them by name.
  useEffect(() => {
    if (user && shouldShowOnboarding()) setTourOpen(true);
  }, [user]);

  const activeRange = useMemo(() => computeRange(datePreset, custom), [datePreset, custom]);

  // Sync the calendar to the filter — when the user picks 7 days / month /
  // last-month / YTD / custom, jump the calendar to the range's end month so
  // the filtered trades are actually visible. "All-time" leaves it alone.
  useEffect(() => {
    if (datePreset === "all") return;
    const target = activeRange.to || activeRange.from;
    if (!target) return;
    const dt = new Date(target + "T00:00:00");
    if (isNaN(dt)) return;
    setCalendarMonth(prev =>
      (prev.getFullYear() === dt.getFullYear() && prev.getMonth() === dt.getMonth())
        ? prev
        : new Date(dt.getFullYear(), dt.getMonth(), 1)
    );
  }, [datePreset, activeRange.from, activeRange.to]);
  const [calendarMonth, setCalendarMonth] = useState(new Date());
  const [autoJumped, setAutoJumped] = useState(false);

  const load = async () => {
    if (!activeId) return;
    try {
      const p = new URLSearchParams({ account_id: activeId });
      if (activeRange.from) p.set("from_date", activeRange.from);
      if (activeRange.to)   p.set("to_date", activeRange.to);
      if (position !== "all") p.set("direction", position);
      const [s, c] = await Promise.all([
        api.get(`/analytics/summary?${p.toString()}`),
        api.get(`/analytics/calendar?${new URLSearchParams({
          account_id: activeId,
          ...(position !== "all" ? { direction: position } : {}),
          from_date: `${calendarMonth.getFullYear()}-${String(calendarMonth.getMonth()+1).padStart(2,"0")}-01`,
          to_date: fmtISO(new Date(calendarMonth.getFullYear(), calendarMonth.getMonth()+1, 0)),
        })}`),
      ]);
      setSummary(s.data);
      setCalendar(c.data);
      setRefreshKey(k => k + 1);
      // Auto-jump calendar to the month with the most recent trade — fixes the
      // "just imported CSV, calendar is empty" confusion when trades are in a
      // past month while the calendar defaults to today.
      if (!autoJumped && Array.isArray(c.data) && c.data.length) {
        const daysWithPnl = c.data.filter(d => d && d.pnl !== 0);
        if (daysWithPnl.length === 0) {
          try {
            const { data: eq } = await api.get(`/analytics/equity?account_id=${activeId}`);
            const rows = eq?.equity || [];
            if (rows.length) {
              const last = rows[rows.length - 1];
              const dt = new Date(last.date);
              if (!isNaN(dt.getTime())) {
                setAutoJumped(true);
                setCalendarMonth(dt);
                const mLabel = dt.toLocaleString("en-US", { month: "long", year: "numeric" });
                try { toast.success(`Showing ${mLabel} — that's where your imported trades are`); } catch {}
              }
            }
          } catch {}
        }
      }
    } catch { toast.error("Failed to load"); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [activeId, datePreset, custom.from, custom.to, position, calendarMonth]);

  const shareLink = async () => {
    try {
      const { data } = await api.post("/share", { account_id: activeId, title: "My Trading PnL" });
      const url = `${window.location.origin}/shared/${data.token}`;
      setShareUrl(url); setShareOpen(true);
      try { await navigator.clipboard.writeText(url); toast.success("Link copied"); } catch {}
    } catch { toast.error("Failed to create share link"); }
  };

  const downloadQR = () => {
    const canvas = document.querySelector("#share-qr-canvas");
    if (!canvas) return;
    const url = canvas.toDataURL("image/png");
    const a = document.createElement("a"); a.href = url; a.download = "tradelog-pnl-qr.png"; a.click();
    toast.success("QR code downloaded");
  };

  const openDay = (iso, cell) => {
    if (!cell || (cell.trades || 0) === 0) return;
    const d = new Date(iso + "T00:00:00");
    const label = d.toLocaleDateString(undefined, { weekday: "long", day: "numeric", month: "long", year: "numeric" });
    setRange({ from: iso, to: iso, title: label, subtitle: "Trades on this date" });
    setRangeOpen(true);
  };

  const active = accounts.find(a => a.id === activeId);
  const displayPnl = pnlType === "gross" ? (summary?.gross_pnl ?? 0) : (summary?.total_pnl ?? 0);
  const equityKey = pnlType === "gross" ? "gross" : "equity";
  const equityData = summary?.equity_curve || [];

  const classicEquity = (
    <div className="rounded-2xl border border-border bg-card p-4 sm:p-6 lg:p-8">
      <div className="flex items-center justify-between mb-4 sm:mb-6">
        <div>
          <p className="text-[10px] sm:text-[11px] tracking-[0.2em] sm:tracking-[0.25em] uppercase text-muted-foreground">Equity Curve</p>
          <h3 className="font-display text-lg sm:text-2xl font-semibold mt-1">
            Cumulative {pnlType === "gross" ? "Gross" : "Net"} P&L
          </h3>
        </div>
      </div>
      <EquityChart data={equityData} chartType={chartType} dataKey={equityKey} />
    </div>
  );

  const netCard = (
    <div className="rounded-2xl border border-border bg-card p-4 sm:p-6">
      <p className="text-[10px] sm:text-[11px] tracking-[0.2em] sm:tracking-[0.25em] uppercase text-emerald-400">Net P&L Curve</p>
      <p className="text-[11px] sm:text-xs text-muted-foreground mb-2">After fees</p>
      <EquityChart data={equityData} chartType={chartType} dataKey="equity" />
    </div>
  );
  const grossCard = (
    <div className="rounded-2xl border border-border bg-card p-4 sm:p-6">
      <p className="text-[10px] sm:text-[11px] tracking-[0.2em] sm:tracking-[0.25em] uppercase text-sky-400">Gross P&L Curve</p>
      <p className="text-[11px] sm:text-xs text-muted-foreground mb-2">Before fees</p>
      <EquityChart data={equityData} chartType={chartType} dataKey="gross" />
    </div>
  );

  const calendarCard = <CalendarView month={calendarMonth} setMonth={setCalendarMonth} cells={calendar} onOpenDay={openDay} account={active} range={activeRange} />;

  return (
    <div className="relative p-4 sm:p-6 lg:p-10 max-w-[1600px] mx-auto overflow-hidden" data-testid="dashboard-page">
      {/* Soft aurora background — same motion language as the landing */}
      <div className="aurora opacity-40" aria-hidden="true" />
      <div className="relative">
      <div data-reveal className="flex items-start justify-between mb-6 sm:mb-8 gap-3 sm:gap-6 flex-wrap">
        <div className="min-w-0">
          <p className="text-[10px] sm:text-[11px] tracking-[0.25em] sm:tracking-[0.3em] uppercase text-muted-foreground">Overview</p>
          <h1 className="font-display text-3xl sm:text-4xl lg:text-6xl font-bold tracking-tight mt-2 truncate">{active?.name || "Dashboard"}</h1>
          <p className="text-sm sm:text-base text-muted-foreground mt-1 sm:mt-2">Your live performance snapshot.</p>
        </div>
        <div className="flex flex-wrap gap-2 w-full sm:w-auto">
          <Button variant="outline" size="sm" className="sm:h-11 sm:px-5 sm:text-base" onClick={async () => {
            try {
              const token = localStorage.getItem("tj_token");
              const url = `${API}/trades/export/pdf?account_id=${activeId}`;
              const resp = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
              if (resp.status === 402) return toast.error("PDF export is a paid feature");
              if (!resp.ok) return toast.error("Export failed");
              const blob = await resp.blob();
              const a = document.createElement("a"); a.href = URL.createObjectURL(blob);
              a.download = "tradelog-pnl.pdf"; a.click(); URL.revokeObjectURL(a.href);
              toast.success("PDF downloaded");
            } catch { toast.error("Export failed"); }
          }} data-testid="export-pdf-btn"><DownloadSimple size={14} className="mr-1" />PDF</Button>
          <Button variant="outline" size="sm" className="sm:h-11 sm:px-5 sm:text-base" onClick={() => setCsvOpen(true)} data-testid="import-csv-btn">Import CSV</Button>
          <Button variant="outline" size="sm" className="sm:h-11 sm:px-5 sm:text-base" onClick={shareLink} data-testid="share-pnl-btn">Share PnL</Button>
          <Button size="sm" onClick={() => setAddOpen(true)} className="rounded-full sm:h-11 sm:px-5 sm:text-base flex-1 sm:flex-initial" data-testid="add-trade-btn">+ Add Trade</Button>
        </div>
      </div>

      {/* Filters row (compact, right-aligned below account) */}
      <div data-reveal data-delay="1" className="grid grid-cols-1 lg:grid-cols-3 gap-5 mb-6 tilt-parent">
        <div className="lg:col-span-2 tilt">
          <DisciplineCard refreshKey={refreshKey} />
        </div>
        <div className="tilt rounded-2xl border border-border bg-card p-4" data-testid="dashboard-filters">
          <div className="flex items-center gap-2 mb-3 text-[11px] tracking-[0.25em] uppercase text-muted-foreground">
            <Funnel size={12} /> Filters
          </div>
          <div className="space-y-2">
            <div>
              <label className="text-[10px] uppercase tracking-wider text-muted-foreground">Date range</label>
              <Select value={datePreset} onValueChange={setDatePreset}>
                <SelectTrigger data-testid="filter-date-range" className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All-time</SelectItem>
                  <SelectItem value="today">Today</SelectItem>
                  <SelectItem value="week">This week</SelectItem>
                  <SelectItem value="month">This month</SelectItem>
                  <SelectItem value="last_month">Last month</SelectItem>
                  <SelectItem value="ytd">Year to date</SelectItem>
                  <SelectItem value="custom">Custom…</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {datePreset === "custom" && (
              <div className="grid grid-cols-2 gap-2">
                <Input type="date" value={custom.from} onChange={e => setCustom({ ...custom, from: e.target.value })} data-testid="filter-from-date" />
                <Input type="date" value={custom.to}   onChange={e => setCustom({ ...custom, to:   e.target.value })} data-testid="filter-to-date" />
              </div>
            )}
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-[10px] uppercase tracking-wider text-muted-foreground">P&L</label>
                <Select value={pnlType} onValueChange={setPnlType}>
                  <SelectTrigger data-testid="filter-pnl-type" className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="net">Net</SelectItem>
                    <SelectItem value="gross">Gross</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider text-muted-foreground">Chart</label>
                <Select value={chartType} onValueChange={setChartType}>
                  <SelectTrigger data-testid="filter-chart-type" className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="area">Area</SelectItem>
                    <SelectItem value="line">Line</SelectItem>
                    <SelectItem value="bars">Bars</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider text-muted-foreground">Position</label>
                <Select value={position} onValueChange={setPosition}>
                  <SelectTrigger data-testid="filter-position" className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    <SelectItem value="long">Long</SelectItem>
                    <SelectItem value="short">Short</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider text-muted-foreground">Layout</label>
                <Select value={layout} onValueChange={setLayout}>
                  <SelectTrigger data-testid="filter-layout" className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="classic">Classic</SelectItem>
                    <SelectItem value="multi">Multi</SelectItem>
                    <SelectItem value="all-in-one">All-in-one</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <Button variant="ghost" size="sm" className="w-full"
              onClick={() => { setDatePreset("all"); setPosition("all"); setPnlType("net"); setChartType("area"); setLayout("classic"); setCustom({ from: "", to: "" }); }}
              data-testid="filter-reset">Reset filters</Button>
          </div>
        </div>
      </div>

      {/* Empty-state banner — filter returned no trades in the selected range.
          Fixes the "I picked 7 days but see nothing / stale data" confusion. */}
      {summary && summary.total_trades === 0 && datePreset !== "all" && (
        <div data-testid="dashboard-empty-range" className="mb-6 rounded-2xl border border-amber-500/30 bg-amber-500/5 p-5 flex flex-wrap items-center gap-4">
          <div className="grid place-items-center w-11 h-11 rounded-lg bg-amber-500/20 border border-amber-500/30 shrink-0">
            <Funnel size={20} className="text-amber-400" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="font-display text-lg font-bold">No trades in this range</p>
            <p className="text-sm text-muted-foreground mt-0.5">
              We couldn't find any trades between <span className="text-foreground font-mono">{activeRange.from || "…"}</span> and <span className="text-foreground font-mono">{activeRange.to || "today"}</span>.
              Try switching the date range or import a CSV for that window.
            </p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <Button size="sm" variant="outline" data-testid="empty-range-alltime" onClick={() => setDatePreset("all")}>
              Show all-time
            </Button>
            <Button size="sm" data-testid="empty-range-import" onClick={() => setCsvOpen(true)} className="rounded-full">
              Import CSV
            </Button>
          </div>
        </div>
      )}

      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-5 mb-6 sm:mb-8">
        <Kpi label={pnlType === "gross" ? "Gross P&L" : "Net P&L"} tone={displayPnl >= 0 ? "up" : "down"}
          icon={displayPnl >= 0 ? TrendUp : TrendDown}
          value={moneyFor(active, displayPnl)}
          testid="kpi-total-pnl" sub={`${summary?.total_trades ?? 0} trades · fees ${moneyFor(active, summary?.total_fees ?? 0)}`} />
        <Kpi label="Win Rate" icon={Target} value={`${summary?.win_rate ?? 0}%`}
          testid="kpi-win-rate" sub={`${summary?.wins ?? 0}W / ${summary?.losses ?? 0}L`} />
        <Kpi label="Profit Factor" icon={ChartLine}
          value={String(summary?.profit_factor ?? 0)} testid="kpi-profit-factor" sub="wins / |losses|" />
        <Kpi label="Best Day" icon={Trophy}
          value={summary?.best_day ? moneyFor(active, summary.best_day.pnl) : "—"}
          testid="kpi-best-day" sub={summary?.best_day?.date || "no data"} />
      </div>

      {/* Chart layout */}
      {layout === "classic" && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 sm:gap-5" data-testid="layout-classic">
          <div className="lg:col-span-2">{classicEquity}</div>
          {calendarCard}
        </div>
      )}
      {layout === "multi" && (
        <div className="space-y-3 sm:space-y-5" data-testid="layout-multi">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 sm:gap-5">{netCard}{grossCard}</div>
          {calendarCard}
        </div>
      )}
      {layout === "all-in-one" && (
        <div className="space-y-3 sm:space-y-5" data-testid="layout-all-in-one">
          {classicEquity}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 sm:gap-5">{netCard}{grossCard}</div>
          {calendarCard}
        </div>
      )}

      {/* Behaviour / timing */}
      <div data-reveal className="grid grid-cols-1 lg:grid-cols-3 gap-3 sm:gap-5 mt-4 sm:mt-6 tilt-parent">
        <div className="lg:col-span-2 tilt"><TimingChart direction={position} /></div>
        <div className="tilt"><MarketHours /></div>
      </div>

      {/* Crypto Heatmap + Forex News on dashboard */}
      <div data-reveal className="grid grid-cols-1 lg:grid-cols-2 gap-3 sm:gap-5 mt-4 sm:mt-6 tilt-parent">
        <div className="tilt"><CryptoHeatmap /></div>
        <div className="tilt"><ForexNews /></div>
      </div>

      {/* Reports */}
      <div data-reveal className="mt-4 sm:mt-6 tilt-parent">
        <div className="tilt"><ReportsCard /></div>
      </div>

      </div>{/* /relative wrapper */}
      <AddTradeDialog open={addOpen} onOpenChange={setAddOpen} onSaved={load} />
      <CSVImportDialog open={csvOpen} onOpenChange={setCsvOpen} onImported={load} />
      <OnboardingTour
        open={tourOpen}
        userName={user?.name}
        onClose={() => setTourOpen(false)}
        onOpenAddTrade={() => setAddOpen(true)}
        onOpenCsv={() => setCsvOpen(true)}
        onGoPropChallenge={() => nav("/prop-challenge")}
      />
      <TradesRangeDialog open={rangeOpen} onOpenChange={setRangeOpen}
        from={range.from} to={range.to} title={range.title} subtitle={range.subtitle} />

      {/* Share dialog with QR */}
      <Dialog open={shareOpen} onOpenChange={setShareOpen}>
        <DialogContent className="max-w-lg text-foreground">
          <DialogHeader><DialogTitle className="font-display text-2xl text-foreground">Your public PnL link</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">Anyone with this link can view your PnL summary. Scan the QR to open on your phone.</p>
          <div className="flex gap-4 mt-2 items-start">
            <div className="p-3 bg-white rounded-lg" data-testid="share-qr-wrap">
              {shareUrl && (
                <QRCodeCanvas id="share-qr-canvas" value={shareUrl} size={160} bgColor="#ffffff" fgColor="#000000" level="M" />
              )}
            </div>
            <div className="flex-1 space-y-2">
              <Input readOnly value={shareUrl} data-testid="share-url-input" className="font-mono text-xs" />
              <div className="grid grid-cols-2 gap-2">
                <Button onClick={async () => { try { await navigator.clipboard.writeText(shareUrl); toast.success("Copied"); } catch {} }}
                  className="rounded-full" data-testid="share-copy-btn">Copy link</Button>
                <Button variant="outline" onClick={downloadQR} data-testid="share-qr-download">Download QR</Button>
              </div>
              <a href={shareUrl} target="_blank" rel="noreferrer">
                <Button variant="ghost" className="w-full" data-testid="share-open-btn">Open link →</Button>
              </a>
            </div>
          </div>
          <DialogFooter />
        </DialogContent>
      </Dialog>
    </div>
  );
}

